import { subscribe, ukPhone, emailOk } from '@/lib/klaviyo';
import { SITE } from '@/data';
import { Field, inputCls, Status } from './forms';
import { Reveal } from './Reveal';
import { useState } from 'react';

export default function GuestList() {
  const [msg, setMsg] = useState({ kind: 'idle', text: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const trap = form.querySelector('[name=website]');
    if (trap && trap.value) return; // bot trap
    setMsg({ kind: 'idle', text: '' });
    if (!emailOk(form.email.value.trim())) {
      setMsg({ kind: 'err', text: 'Pop in a valid email address.' });
      return;
    }
    const firstName = form.firstName.value.trim();
    if (firstName.length < 2) {
      setMsg({ kind: 'err', text: 'Tell us your first name.' });
      return;
    }
    const ph = ukPhone(form.phone.value);
    if (ph.replace(/\D/g, '').length < 10) {
      setMsg({ kind: 'err', text: 'Pop in a valid mobile number.' });
      return;
    }
    const btn = form.querySelector('[type=submit]');
    const label = btn.textContent;
    btn.disabled = true;
    subscribe(SITE.klaviyo.guestListId, form.email.value.trim(), 'Website guest list', { first_name: firstName, mobile_number: ph })
      .then(() => {
        form.reset();
        setMsg({ kind: 'ok', text: 'Nearly there — check your inbox (and spam) and click the confirm link to lock in your spot.' });
      })
      .catch(() => setMsg({ kind: 'err', text: "That didn't go through. Please try again in a moment." }))
      .finally(() => {
        btn.disabled = false;
        btn.textContent = label;
      });
  };

  return (
    <section id="guestlist" className="mx-auto max-w-[1280px] scroll-mt-24 px-6 pt-24 md:pt-32" data-testid="guestlist-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-[32px] border border-ivory/10 bg-plum-card/70 p-8 backdrop-blur-xl md:p-14">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-mauve/20 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />

          <div className="relative grid grid-cols-1 items-stretch gap-10 lg:grid-cols-12 lg:gap-8">
            <div className="spotlight relative aspect-[3/4] overflow-hidden rounded-3xl lg:col-span-4 lg:aspect-auto lg:h-auto" data-testid="guestlist-photo">
              <img
                src="/assets/nyah-live-mic.webp"
                alt="Princess Nyah live on stage with the mic"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-top md:object-[50%_18%]"
              />
              <span className="absolute bottom-4 left-4 z-10 rounded-full border border-ivory/15 bg-plum-deep/70 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-gold backdrop-blur-md">
                Nyah · live
              </span>
            </div>

            <div className="flex flex-col items-start justify-center gap-5 lg:col-span-4">
              <span className="font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-gold">The guest list</span>
              <h2 className="font-display text-5xl uppercase leading-[0.95] text-ivory md:text-6xl">
                BE FIRST IN <span className="text-mauve">THE CAR</span>
              </h2>
              <p className="max-w-[460px] text-lg text-ivory/70">
                Every new Top 10 lands in your inbox before anyone else hears it. Mondays, 6PM.
              </p>
            </div>

            <form className="flex flex-col justify-center gap-4 lg:col-span-4" noValidate onSubmit={handleSubmit} data-testid="guestlist-form">
              <Field label="First name" htmlFor="gl-name">
                <input id="gl-name" name="firstName" type="text" autoComplete="given-name" placeholder="What do we call you?" required className={inputCls} data-testid="guestlist-name-input" />
              </Field>
              <Field label="Email" htmlFor="gl-email">
                <input id="gl-email" name="email" type="email" autoComplete="email" placeholder="you@email.com" required className={inputCls} data-testid="guestlist-email-input" />
              </Field>
              <Field label="Mobile" htmlFor="gl-phone">
                <input id="gl-phone" name="phone" type="tel" autoComplete="tel" placeholder="07…" required className={inputCls} data-testid="guestlist-phone-input" />
              </Field>
              <div className="absolute left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="gl-web">Leave this empty</label>
                <input id="gl-web" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <button
                type="submit"
                data-testid="guestlist-submit"
                className="mt-2 rounded-full bg-gold px-7 py-4 font-extrabold text-plum transition-transform duration-200 hover:scale-[1.03] hover:shadow-[0_0_36px_rgba(232,184,106,0.5)] disabled:opacity-60"
              >
                Put me on the guest list
              </button>
              <Status msg={msg} />
              <p className="text-[13px] text-ivory/45">No spam. Unsubscribe any time.</p>
            </form>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
